import { type MouseEvent, useCallback, useMemo } from 'react';

import { type SavedFilter } from '@air/api/types';
import { PencilIcon, Trash2Icon } from '@air/design-icons';
import { type ContextMenuOption } from '@air/primitive-context-menu';
import { useAirModal } from '@air/provider-modal';

import { DeleteSavedFilterModal } from '~/components/SavedFilter/DeleteSavedFilterModal';
import { EditSavedFilterModal } from '~/components/SavedFilter/EditSavedFilterModal';
import { useCurrentWorkspacePermissionsContext } from '~/providers/CurrentWorkspacePermissionsProvider';
import { canDeleteSavedFilters, canEditSavedFilters } from '~/utils/permissions/workspacePermissions';

export type UsePrivateSearchSubSideNavSavedSearchItemMenuOptionsParams = {
  savedSearch: SavedFilter;
};

export const usePrivateSearchSubSideNavSavedSearchItemMenuOptions = ({
  savedSearch,
}: UsePrivateSearchSubSideNavSavedSearchItemMenuOptionsParams) => {
  const { data: permissions } = useCurrentWorkspacePermissionsContext();
  const [showDeleteSavedFilterModal] = useAirModal(DeleteSavedFilterModal);
  const [showEditSavedFilterModal] = useAirModal(EditSavedFilterModal);
  const canEdit = canEditSavedFilters(permissions);
  const canDelete = canDeleteSavedFilters(permissions);

  const handleEdit = useCallback(() => {
    showEditSavedFilterModal({ savedFilter: savedSearch });
  }, [savedSearch, showEditSavedFilterModal]);

  const handleEditClick = useCallback(
    (event: MouseEvent) => {
      event.preventDefault();
      event.stopPropagation();
      handleEdit();
    },
    [handleEdit],
  );

  const handleDelete = useCallback(() => {
    showDeleteSavedFilterModal({ savedFilter: savedSearch });
  }, [savedSearch, showDeleteSavedFilterModal]);

  const menuOptions = useMemo<ContextMenuOption[]>(() => {
    const options: ContextMenuOption[] = [];

    if (canEdit) {
      options.push({
        id: 'edit',
        label: 'Edit',
        onSelect: handleEdit,
        prefix: <PencilIcon aria-hidden size="sm" />,
        type: 'item',
      });
    }

    if (canDelete) {
      if (options.length > 0) {
        options.push({
          id: 'delete-separator',
          type: 'separator',
        });
      }

      options.push({
        className: 'ads:text-error text-red-9',
        id: 'delete',
        label: 'Delete from workspace',
        onSelect: handleDelete,
        prefix: <Trash2Icon aria-hidden size="sm" />,
        type: 'item',
      });
    }

    return options;
  }, [canDelete, canEdit, handleDelete, handleEdit]);

  return {
    canEdit,
    handleEditClick,
    menuOptions,
  };
};
